import React, { useState } from 'react';
import { AutoComplete, Form, Input } from 'antd';
import type { FormInstance } from 'antd';
import { CarOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TransportFormSection from './TransportFormSection';
import TransportStatusPicker from './TransportStatusPicker';
import type { SharedTransportFormValues } from './sharedTransportFormUtils';

interface CustomerOption {
  name: string;
}

interface TransportCompanyOption {
  id: string;
  name: string;
  nip?: string | null;
}

interface TransportCarrierSectionProps {
  /** Section number (owner 2026-10-10: „Gdzie jedzie” shifts the following sections). */
  step?: number;
  form: FormInstance<SharedTransportFormValues>;
  customerOptions: CustomerOption[];
  transportCompanyOptions: TransportCompanyOption[];
  onCustomerTouched: () => void;
  /**
   * Decision 2026-10-04: a new truck starts „Zaplanowany” without a choice;
   * the status picker appears once the truck is saved (edit mode).
   */
  isEditMode?: boolean;
  /** Round 3 (U22): „Fabryka → magazyn” has no customer — the field is hidden. */
  hideCustomer?: boolean;
}

const TransportCarrierSection: React.FC<TransportCarrierSectionProps> = ({
  step = 3,
  form,
  customerOptions,
  transportCompanyOptions,
  onCustomerTouched,
  isEditMode = true,
  hideCustomer = false,
}) => {
  const { t } = useTranslation(['transport']);
  // Round 3 (U16): the carrier list opens on focus and on click, with an
  // arrow, so the field does not read as free text.
  const [carrierOpen, setCarrierOpen] = useState(false);
  const transportCompany = Form.useWatch('transportCompany', form);
  const summary = String(transportCompany ?? '').trim() || undefined;

  return (
    <TransportFormSection sectionKey="carrier" step={step} icon={<CarOutlined />} label={t('transport:sections.carrier')} summary={summary}>
      {/* Owner 2026-10-10: one tidy row — carrier, contact, (customer), status. */}
      <div className="transport-fields-grid">
        <div>
          <Form.Item
            name="transportCompany"
            label={t('transport:fields.transportCompany')}
            rules={[{
              required: true,
              whitespace: true,
              message: t('transport:validation.companyRequired'),
            }]}
          >
            <AutoComplete
              allowClear
              // Round 2 (U5): the list opens on focus above the save bar,
              // wide enough for long names; a new name is typed freely.
              open={carrierOpen && transportCompanyOptions.length > 0}
              onDropdownVisibleChange={setCarrierOpen}
              onFocus={() => setCarrierOpen(true)}
              onBlur={() => setCarrierOpen(false)}
              onClick={() => setCarrierOpen(true)}
              suffixIcon={<DownOutlined />}
              popupMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 280, zIndex: 1100 }}
              listHeight={320}
              notFoundContent={t('transport:carrier.newCarrierHint')}
              data-testid="transport-company-input"
              placeholder={t('transport:placeholders.transportCompany')}
              options={transportCompanyOptions.map((company) => ({
                value: company.name,
                label: company.nip ? `${company.name} · NIP ${company.nip}` : company.name,
              }))}
              filterOption={(input, option) => {
                const needle = input.trim().toLowerCase();
                if (!needle) return true;
                const optValue = String(option?.value ?? '').toLowerCase();
                const optLabel = String(option?.label ?? '').toLowerCase();
                return optValue.includes(needle) || optLabel.includes(needle);
              }}
              onSelect={(value) => {
                const picked = transportCompanyOptions.find(
                  (company) => company.name.toLowerCase() === String(value).toLowerCase(),
                );
                form.setFieldsValue({ transportCompanyId: picked?.id ?? null });
                setCarrierOpen(false);
              }}
              onChange={(value) => {
                // Clear the hidden id whenever the typed text no longer
                // matches a registered carrier exactly. Without this, picking
                // "Acme" (id=X) and then editing the text to "Acmee" would
                // keep the stale id in the payload. The backend would then
                // mirror Acme's name back onto a row the user thought was a
                // new carrier, silently discarding their edit.
                const normalized = String(value ?? '').trim().toLowerCase();
                const matched = normalized
                  ? transportCompanyOptions.find(
                      (company) => company.name.toLowerCase() === normalized,
                    )
                  : null;
                form.setFieldsValue({ transportCompanyId: matched?.id ?? null });
              }}
            />
          </Form.Item>
          {/* Hidden-but-registered field: the Form.Item owns the value so
              setFieldsValue + onFinish both see it. A plain <Input /> (not
              type="hidden") keeps Ant's controlled-input contract intact. */}
          <Form.Item name="transportCompanyId" hidden noStyle>
            <Input />
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name="transportContact"
            label={t('transport:fields.transportContact')}
          >
            <Input placeholder={t('transport:placeholders.transportContact')} />
          </Form.Item>
        </div>
        <div style={hideCustomer ? { display: 'none' } : undefined}>
          <Form.Item
            name="customer"
            label={t('transport:fields.customer')}
          >
            <AutoComplete
              allowClear
              placeholder={t('transport:placeholders.customer')}
              options={customerOptions.map((customer) => ({
                value: customer.name,
                label: customer.name,
              }))}
              filterOption={(input, option) =>
                String(option?.value ?? '').toLowerCase().includes(input.trim().toLowerCase())
              }
              // Any manual edit (typing, clearing, or picking) stops the
              // create-mode auto-fill from the selected orders.
              onChange={onCustomerTouched}
            />
          </Form.Item>
        </div>
        <div>
          {isEditMode ? (
            <Form.Item
              name="status"
              label={t('transport:fields.status')}
              rules={[{ required: true, message: t('transport:validation.statusRequired') }]}
            >
              <TransportStatusPicker />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="status" hidden noStyle>
                <Input />
              </Form.Item>
              <Form.Item label={t('transport:fields.status')}>
                <span className="transport-status-pill" data-testid="transport-status-on-create">
                  <span className="transport-status-pill__dot" aria-hidden="true" />
                  <span className="transport-status-pill__name">{t('transport:fields.statusPlanned')}</span>
                  <span className="transport-status-pill__hint">{t('transport:fields.statusOnCreateHint')}</span>
                </span>
              </Form.Item>
            </>
          )}
        </div>
      </div>
    </TransportFormSection>
  );
};

export default TransportCarrierSection;
